import React, { useState } from "react";
import AddBrand from "./AddBrand";
import EditBrand from "./EditBrand";
import DeleteBrand from "./DeleteBrand";
import useRead from "../../hooks/useRead";
import Spinner from "../../components/spinner";
import Errors from "../errors";
import { Button, Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import Pagination from "../../components/elements/pagination";
import { createSearchParams, useSearchParams } from "react-router-dom";
import SwitchToggle from "../../components/elements/switchtoggle/SwitchToggle";
import toast from "react-hot-toast";
import useCreate from "../../hooks/useCreate";
import { useMutation } from "@tanstack/react-query";
import { exportBrands } from "../../apis/brands";
import PrimaryButton from "../../components/elements/buttons/PrimaryButton";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Brands = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialState = {
    limit: 10,
    pageNo: searchParams.get("pageNo") || 0,
    searchQuery: searchParams.get("searchQuery") || "",
  };
  const [isOpenAddModal, setIsOpenAddModal] = useState(false);
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [brandId, setBrandId] = useState(0);
  const [isDeletedSuccess, setIsDeletedSuccess] = useState(false);
  const [editData, setEditData] = useState(null);
  const [brands, setBrand] = useState([]);
  const [currentPage, setCurrentPage] = useState(parseInt(initialState.pageNo));

  const {
    data: handleGetProductList,
    paramsObject,
    setGetListParams,
  } = useRead({
    url: "brand/list",
    initialData: initialState,
    method: "GET",
    onSuccess: (data) => {
      setBrand(data.data);
    },
    onError: (err) => {
      console.error("Error fetching brands:", err);
    },
  });

  const total_count = handleGetProductList?.data?.totalCount;

  const exportBrandsMutation = useMutation(
    (params) => exportBrands(params),
    {
      onSuccess: (res) => {
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = "brands-export.csv";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Export downloaded");
      },
      onError: (err) => {
        toast.error(err?.message || "Export failed");
      },
    }
  );

  const handleExport = () => {
    const exportParams = { ...paramsObject };
    if (exportParams.pageNo !== undefined) delete exportParams.pageNo;
    exportParams.limit = exportParams.limit ?? 9999;
    exportBrandsMutation.mutate(exportParams);
  };

  const handleAddBrand = () => {
    setIsOpenAddModal(true);
  };

  const handleEditBrand = (data) => {
    setIsOpenEditModal(true);
    setEditData({
      brand_id: data.brand_id,
      brand_name: data.name,
      brand_url: data.brand_url,
      logo: data.logo, // document_id
      logo_url: data.logo_url,
    });
  };

  const handleCloseAddModal = () => {
    setIsOpenAddModal(false);
  };

  const handleCloseEditModal = () => {
    setIsOpenEditModal(false);
    setEditData(null);
  };

  const handleOpenDeleteModal = (id) => {
    setOpenDeleteModal(true);
    setBrandId(id);
  };

  const handleModalClose = () => {
    setOpenDeleteModal(false);
  };

  const deletedSuccess = () => {
    handleModalClose();
    setIsDeletedSuccess(true);
  };

  const handlePageClick = (val) => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    const pageNo = val.selected;
    setCurrentPage(pageNo);
    setSearchParams(
      createSearchParams({
        pageNo: pageNo,
        searchQuery: paramsObject.searchQuery || "",
      })
    );

    let PageNo = {
      ...paramsObject,
      pageNo: pageNo,
    };
    setGetListParams(PageNo);
  };

  const handleSearchVal = (e) => {
    const searchQuery = e.target.value;
    const nextParams = {
      ...paramsObject,
      pageNo: 0,
      searchQuery,
    };

    setCurrentPage(0);
    setSearchParams(
      createSearchParams({
        pageNo: 0,
        searchQuery,
      })
    );
    setGetListParams(nextParams);
  };

  // Just before the return statement
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="category">
        <div className="category__head headingBorder__b">
          <h2 className="titleHeading">Brands</h2>
        </div>
        <div className="my-4 flex flex-wrap items-center justify-between gap-3">
          <input
            onChange={handleSearchVal}
            value={paramsObject.searchQuery || ""}
            type="text"
            placeholder="Search By Key Word...."
            className="input w-40"
          />
          <div className="flex gap-3">
            <PrimaryButton
              label={exportBrandsMutation.isPending ? "Exporting…" : "Export"}
              onClick={handleExport}
              disabled={exportBrandsMutation.isPending}
            />
            <Button
              onClick={handleAddBrand}
              variant="outlined"
              startIcon={<AddIcon />}
              className="primaryBtn-outlined"
            >
              Add Brand
            </Button>
          </div>
        </div>
      </div>
      <div>
        {handleGetProductList?.isLoading ? (
          <Spinner />
        ) : handleGetProductList?.status === "error" ? (
          <Errors errorObject={handleGetProductList?.error} inline />
        ) : (
          <div className="overflow-x-auto bg-white rounded-lg shadow overflow-y-auto relative">
            <table className="border-collapse table-auto w-full whitespace-no-wrap bg-white table-striped relative">
              <thead>
                <tr className="text-left">
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs">
                    Sr.no
                  </th>
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs text-center">
                    <p className="w-40">Brand Image</p>
                  </th>
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs">
                    <p className="w-40">Brand Name</p>
                  </th>
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs">
                    <p className="w-40">Brand URL</p>
                  </th>
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs text-center">
                    <p className="w-24">Status</p>
                  </th>
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {handleGetProductList?.data?.data?.map((brandData, idx) => {
                  const { brand_id, name, slug, brand_url, logo, logo_url } =
                    brandData;
                  return (
                    <tr key={idx}>
                      <td className="border-dashed border-t border-gray-200 userId">
                        <span className="text-gray-700 px-6 py-3 flex items-center text-sm">
                          {initialState.limit * currentPage + idx + 1}
                        </span>
                      </td>
                      <td className="border-dashed border-t border-gray-200 firstName">
                        <div className="w-20 h-20 mx-auto p-4">
                          {logo_url ? (
                            <img
                              src={logo_url}
                              alt={`${name}`}
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <div className="w-full h-full text-center bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-medium rounded">
                              No Image
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="border-dashed border-t border-gray-200 firstName">
                        <span className="text-gray-700 px-6 py-3 flex items-center text-sm">
                          {name}
                        </span>
                      </td>
                      <td className="border-dashed border-t border-gray-200 firstName">
                        <span className="text-gray-700 px-6 py-3 flex items-center text-sm">
                          {brand_url ? brand_url : "---"}
                        </span>
                      </td>
                      <td>
                        <BrandTableStatusCell data={brandData} />
                      </td>
                      <td className="border-dashed border-t border-gray-200 phoneNumber">
                        <div className="flex items-center space-x-5 justify-center">
                          <div>
                            <IconButton
                              onClick={() => handleEditBrand(brandData)}
                            >
                              <EditIcon />
                            </IconButton>
                          </div>
                          <div onClick={() => handleOpenDeleteModal(brand_id)}>
                            <IconButton>
                              <DeleteIcon />
                            </IconButton>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="my-7 flex justify-center items-center">
          <Pagination
            currentPage={currentPage}
            onPageChange={handlePageClick}
            pageCount={Math.ceil(total_count / initialState.limit)}
            limit={initialState.limit}
          />
        </div>
      </div>
      <AddBrand open={isOpenAddModal} onClose={handleCloseAddModal} />
      <EditBrand
        open={isOpenEditModal}
        onClose={handleCloseEditModal}
        editData={editData}
      />
      <DeleteBrand
        open={openDeleteModal}
        onClose={handleModalClose}
        id={brandId}
        onDelete={deletedSuccess}
      />
      <Snackbar
        open={isDeletedSuccess}
        autoHideDuration={4000}
        onClose={() => setIsDeletedSuccess(false)}
      >
        <Alert
          onClose={() => setIsDeletedSuccess(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Brand Deleted Successfully
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Brands;

const BrandTableStatusCell = ({ data }) => {
  const [status, setStatus] = useState(data.status);

  const { setDataToServer: updateStatusReq } = useCreate({
    url: "brand/update_status",
    onSuccess: () => {
      toast.success("Brand Status Updated Successfully", {
        position: "top-right",
        duration: 3000,
      });
    },
    onError: () => {
      toast.error("Failed to Update Brand Status", {
        position: "top-right",
        duration: 3000,
      });
    },
  });

  const handleStatusChange = (e) => {
    const newStatus = e.target.checked;
    setStatus(newStatus);
    updateStatusReq.mutate({
      brand_id: data.brand_id,
      status: newStatus,
    });
  };

  return (
    <div className="flex items-center justify-center">
      <SwitchToggle
        label=""
        checkedValue={status}
        onChange={(e) => handleStatusChange(e)}
      />
    </div>
  );
};
